import CryptoJS from 'crypto-js';
import type { AuthTokens } from '../../types/api/auth';

/**
 * Token Manager for secure token handling
 * 
 * Features:
 * - Secure token storage with encryption
 * - Token validation and expiry checking
 * - Automatic token refresh scheduling
 * - Token rotation and cleanup
 * - Cross-tab synchronization
 * - Security event monitoring
 */
export class TokenManager {
  private static instance: TokenManager;
  private encryptionKey: string;
  private refreshTimeoutId: number | null = null;
  private storageEventListener: ((event: StorageEvent) => void) | null = null;

  private constructor() {
    this.encryptionKey = this.generateEncryptionKey();
    this.setupStorageListener();
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  /**
   * Generate encryption key for token storage
   */
  private generateEncryptionKey(): string {
    // In production, this should be derived from a more secure source
    const deviceFingerprint = this.getDeviceFingerprint();
    const sessionId = this.getSessionId();
    return CryptoJS.SHA256(`${deviceFingerprint}:${sessionId}`).toString();
  }

  /**
   * Get device fingerprint for encryption key generation
   */
  private getDeviceFingerprint(): string {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint', 2, 2);
    }
    
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      canvas.toDataURL(),
    ].join('|');

    return CryptoJS.SHA256(fingerprint).toString();
  }

  /**
   * Get or create session ID
   */
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = this.generateRandomId();
      sessionStorage.setItem('sessionId', sessionId);
    }
    return sessionId;
  }

  /**
   * Generate random ID
   */
  private generateRandomId(): string {
    return CryptoJS.lib.WordArray.random(16).toString();
  }

  /**
   * Encrypt data
   */
  private encrypt(data: string): string {
    try {
      return CryptoJS.AES.encrypt(data, this.encryptionKey).toString();
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  /**
   * Decrypt data
   */
  private decrypt(encryptedData: string): string {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedData, this.encryptionKey);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      
      if (!decrypted) {
        throw new Error('Decryption resulted in empty string');
      }
      
      return decrypted;
    } catch (error) {
      console.error('Decryption failed:', error);
      throw new Error('Failed to decrypt data');
    }
  }

  /**
   * Store tokens securely
   */
  public storeTokens(tokens: AuthTokens, persistent: boolean = false): void {
    try {
      const tokenData = {
        ...tokens,
        timestamp: Date.now(),
      };

      const encryptedData = this.encrypt(JSON.stringify(tokenData));
      const storage = persistent ? localStorage : sessionStorage;
      
      storage.setItem('auth_tokens', encryptedData);
      storage.setItem('token_storage_type', persistent ? 'persistent' : 'session');
      
      // Schedule token refresh
      this.scheduleTokenRefresh(tokens);
      
      console.debug('Tokens stored securely');
    } catch (error) {
      console.error('Failed to store tokens:', error);
      throw new Error('Failed to store authentication tokens');
    }
  }

  /**
   * Retrieve stored tokens
   */
  public getStoredTokens(): AuthTokens | null {
    try {
      // Try session storage first, then local storage
      let encryptedData = sessionStorage.getItem('auth_tokens');
      if (!encryptedData) {
        encryptedData = localStorage.getItem('auth_tokens');
      }

      if (!encryptedData) {
        return null;
      }

      const decryptedData = this.decrypt(encryptedData);
      const tokenData = JSON.parse(decryptedData);

      // Validate token structure
      if (!this.isValidTokenStructure(tokenData)) {
        console.warn('Invalid token structure found, clearing tokens');
        this.clearTokens();
        return null;
      }

      // Check if tokens are expired
      if (this.isTokenExpired(tokenData.accessToken)) {
        console.info('Stored tokens are expired');
        return tokenData; // Return expired tokens for refresh attempt
      }

      return {
        accessToken: tokenData.accessToken,
        refreshToken: tokenData.refreshToken,
        expiresIn: tokenData.expiresIn,
        tokenType: tokenData.tokenType,
        sessionId: tokenData.sessionId || '',
      };
    } catch (error) {
      console.error('Failed to retrieve tokens:', error);
      this.clearTokens(); // Clear corrupted tokens
      return null;
    }
  }

  /**
   * Validate token structure
   */
  private isValidTokenStructure(tokenData: any): boolean {
    return !!(
      tokenData &&
      typeof tokenData.accessToken === 'string' &&
      typeof tokenData.refreshToken === 'string' &&
      typeof tokenData.expiresIn === 'number' &&
      tokenData.tokenType === 'Bearer'
    );
  }

  /**
   * Check if token is expired
   */
  public isTokenExpired(token: string): boolean {
    try {
      const payload = this.parseTokenPayload(token);
      if (!payload || !payload.exp) {
        return true;
      }

      const expiry = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const buffer = 5 * 60 * 1000; // 5 minute buffer

      return now >= (expiry - buffer);
    } catch (error) {
      console.warn('Failed to check token expiry:', error);
      return true;
    }
  }

  /**
   * Parse token payload
   */
  private parseTokenPayload(token: string): any {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid token format');
      }

      const payload = parts[1];
      const decoded = atob(payload);
      return JSON.parse(decoded);
    } catch (error) {
      console.error('Failed to parse token payload:', error);
      return null;
    }
  }

  /**
   * Get token expiry time
   */
  public getTokenExpiry(token: string): Date | null {
    try {
      const payload = this.parseTokenPayload(token);
      if (payload && payload.exp) {
        return new Date(payload.exp * 1000);
      }
      return null;
    } catch (error) {
      console.error('Failed to get token expiry:', error);
      return null;
    }
  }

  /**
   * Get time until token expires
   */
  public getTimeUntilExpiry(token: string): number {
    const expiry = this.getTokenExpiry(token);
    if (!expiry) return 0;

    const now = Date.now();
    const expiryTime = expiry.getTime();
    
    return Math.max(0, expiryTime - now);
  }

  /**
   * Schedule automatic token refresh
   */
  private scheduleTokenRefresh(tokens: AuthTokens): void {
    // Clear existing timeout
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
    }

    const timeUntilExpiry = this.getTimeUntilExpiry(tokens.accessToken);
    const refreshTime = timeUntilExpiry - (10 * 60 * 1000); // Refresh 10 minutes before expiry

    if (refreshTime > 0) {
      this.refreshTimeoutId = window.setTimeout(() => {
        this.dispatchTokenRefreshEvent();
      }, refreshTime);

      console.debug(`Token refresh scheduled in ${refreshTime / 1000} seconds`);
    }
  }

  /**
   * Dispatch token refresh event
   */
  private dispatchTokenRefreshEvent(): void {
    const event = new CustomEvent('token:refresh_needed', {
      detail: { timestamp: new Date().toISOString() }
    });
    window.dispatchEvent(event);
  }

  /**
   * Clear stored tokens
   */
  public clearTokens(): void {
    try {
      // Clear from both storages
      sessionStorage.removeItem('auth_tokens');
      sessionStorage.removeItem('token_storage_type');
      localStorage.removeItem('auth_tokens');
      localStorage.removeItem('token_storage_type');

      // Clear refresh timeout
      if (this.refreshTimeoutId) {
        clearTimeout(this.refreshTimeoutId);
        this.refreshTimeoutId = null;
      }

      console.debug('Tokens cleared');
    } catch (error) {
      console.error('Failed to clear tokens:', error);
    }
  }

  /**
   * Rotate tokens (for security)
   */
  public rotateTokens(newTokens: AuthTokens): void {
    const currentTokens = this.getStoredTokens();
    if (currentTokens) {
      // Store old tokens for potential rollback
      const oldTokensKey = `old_tokens_${Date.now()}`;
      const encryptedOldTokens = this.encrypt(JSON.stringify(currentTokens));
      sessionStorage.setItem(oldTokensKey, encryptedOldTokens);

      // Clean up old token backups (keep only last 3)
      this.cleanupOldTokens();
    }

    // Store new tokens
    const isPersistent = localStorage.getItem('token_storage_type') === 'persistent';
    this.storeTokens(newTokens, isPersistent);

    console.debug('Tokens rotated successfully');
  }

  /**
   * Clean up old token backups
   */
  private cleanupOldTokens(): void {
    try {
      const oldTokenKeys = Object.keys(sessionStorage)
        .filter(key => key.startsWith('old_tokens_'))
        .sort()
        .reverse();

      // Keep only the 3 most recent backups
      for (let i = 3; i < oldTokenKeys.length; i++) {
        sessionStorage.removeItem(oldTokenKeys[i]);
      }
    } catch (error) {
      console.warn('Failed to cleanup old tokens:', error);
    }
  }

  /**
   * Setup cross-tab synchronization
   */
  private setupStorageListener(): void {
    this.storageEventListener = (event: StorageEvent) => {
      if (event.key === 'auth_tokens') {
        if (event.newValue) {
          // Tokens updated in another tab
          this.dispatchTokenUpdateEvent('updated');
        } else {
          // Tokens cleared in another tab
          this.dispatchTokenUpdateEvent('cleared');
        }
      }
    };

    window.addEventListener('storage', this.storageEventListener);
  }

  /**
   * Dispatch token update event for cross-tab sync
   */
  private dispatchTokenUpdateEvent(action: 'updated' | 'cleared'): void {
    const event = new CustomEvent('token:cross_tab_update', {
      detail: { action, timestamp: new Date().toISOString() }
    });
    window.dispatchEvent(event);
  }

  /**
   * Validate token signature (basic validation)
   */
  public validateTokenSignature(token: string): boolean {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return false;
      }

      // Basic structure validation
      const header = JSON.parse(atob(parts[0]));
      const payload = JSON.parse(atob(parts[1]));

      return !!(
        header &&
        header.alg &&
        header.typ === 'JWT' &&
        payload &&
        payload.exp &&
        payload.iat
      );
    } catch (error) {
      console.error('Token signature validation failed:', error);
      return false;
    }
  }

  /**
   * Get token metadata
   */
  public getTokenMetadata(token: string): any {
    try {
      const payload = this.parseTokenPayload(token);
      if (!payload) return null;

      return {
        userId: payload.sub,
        email: payload.email,
        role: payload.role,
        issuedAt: new Date(payload.iat * 1000),
        expiresAt: new Date(payload.exp * 1000),
        issuer: payload.iss,
        audience: payload.aud,
      };
    } catch (error) {
      console.error('Failed to get token metadata:', error);
      return null;
    }
  }

  /**
   * Check if user has permission based on token
   */
  public hasPermission(token: string, permission: string): boolean {
    try {
      const payload = this.parseTokenPayload(token);
      if (!payload || !payload.permissions) {
        return false;
      }

      return payload.permissions.includes(permission);
    } catch (error) {
      console.error('Permission check failed:', error);
      return false;
    }
  }

  /**
   * Dispose of token manager resources
   */
  public dispose(): void {
    // Clear refresh timeout
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = null;
    }

    // Remove storage event listener
    if (this.storageEventListener) {
      window.removeEventListener('storage', this.storageEventListener);
      this.storageEventListener = null;
    }

    console.debug('TokenManager disposed');
  }

  /**
   * Health check
   */
  public healthCheck(): { healthy: boolean; message: string; details?: any } {
    try {
      const canEncrypt = this.testEncryption();
      const canAccessStorage = this.testStorageAccess();
      const tokens = this.getStoredTokens();

      return {
        healthy: canEncrypt && canAccessStorage,
        message: canEncrypt && canAccessStorage 
          ? 'TokenManager is healthy' 
          : 'TokenManager has issues',
        details: {
          encryption: canEncrypt,
          storageAccess: canAccessStorage,
          hasStoredTokens: !!tokens,
          tokenExpired: tokens ? this.isTokenExpired(tokens.accessToken) : null,
        },
      };
    } catch (error) {
      return {
        healthy: false,
        message: 'TokenManager health check failed',
        details: { error: error instanceof Error ? error.message : 'Unknown error' },
      };
    }
  }

  /**
   * Test encryption functionality
   */
  private testEncryption(): boolean {
    try {
      const testData = 'test_encryption';
      const encrypted = this.encrypt(testData);
      const decrypted = this.decrypt(encrypted);
      return decrypted === testData;
    } catch (error) {
      return false;
    }
  }

  /**
   * Test storage access
   */
  private testStorageAccess(): boolean {
    try {
      const testKey = 'test_token_storage';
      sessionStorage.setItem(testKey, 'test');
      const retrieved = sessionStorage.getItem(testKey);
      sessionStorage.removeItem(testKey);
      return retrieved === 'test';
    } catch (error) {
      return false;
    }
  }
}
